import React, { createContext, useMemo, useState } from "react";
import { InfoCircleTwoTone, QuestionCircleOutlined } from '@ant-design/icons';
import { ReactComponent as CertaintyOk } from './media/certaintyOk.svg';

export const AppContext = createContext();  //exporting context object

// Index des étapes du tutoriel.
// ATTENTION : S'il y a rajout/suppression de steps, il faut mettre à jour ces index ET vérifier que l'ordre correspond toujours au tableau "steps" ci-dessous
export const TUTO_STEPS = {
    WELCOME: 0,            // ToolsScreen : bouton tutoriel
    NEWS: 1,               // ToolsScreen : les nouveautés de la nouvelle interface
    NEW_SALARIE: 2,        // ToolsScreen : bouton "Nouveau salarié"
    SEARCH_SALARIE: 3,     // ToolsScreen : table de recherche des salariés
    TEST_VISITE: 4,        // ToolsScreen : bouton "Visite de test" (clic requis)
    VISITE_TYPE: 5,        // VisiteTypeSelectionModal : choix du type de visite (clic requis)
    INFO_MODAL: 6,         // UpdateVisite : fenêtre d'aide (checkbox "Afficher les prochaines fenêtres d'aide")
    INFO_BUTTON: 7,        // UpdateVisite : bouton pour réafficher la fenêtre d'aide (clic possible)
    METIER_SEARCH: 8,      // UpdateVisite/Metiers : barre de recherche de métier (le métier "Désamianteur" est ajouté automatiquement)
    METIER_PERIODES: 9,    // UpdateVisite/Metiers : carte du métier ajouté (périodes d'exercice)
    VALIDATE_METIER: 10,   // UpdateVisite : bouton "Valider cette étape" (clic requis)
    TACHES: 11,            // UpdateVisite/Taches : sélection des tâches du métier
    ADD_TACHE: 12,         // UpdateVisite/Taches : bouton "Ajouter une tâche potentielle"
    VALIDATE_ALL: 13,      // UpdateVisite : bouton "Valider toutes les étapes" (clic requis)
    SYNTHESE: 14,          // UpdateVisite : arrivée sur la synthèse de la visite
    QUICK_MODE: 15,        // UpdateVisite : mode rapide (clic requis pour le désactiver)
    STEPS_NAV: 16,         // UpdateVisite : navigation entre les étapes (clic requis sur "Examen clinique orienté")
    EMAIL_PRE_VISITE: 17,  // UpdateVisite : questionnaire de pré-visite (clic requis)
    EMAIL_READY: 18,       // CreateMailVisite : email de pré-visite généré (lien + QR code)
    END: 19,               // Fin du tutoriel
};

// Contenu de l'étape de recherche d'un salarié, qui diffère selon le mode du service :
// en mode anonymisé, aucune donnée nominative n'est enregistrée, la recherche se fait donc uniquement par identifiant (RAVAIL ou identifiant lié).
export const SEARCH_SALARIE_CONTENT = {
    default: <span>Retrouvez ici <b>tous les salariés de votre service</b>. Utilisez la barre de recherche (nom, prénom, INS, email...) puis <b>double-cliquez</b> sur une ligne pour ouvrir le dossier du salarié.</span>,
    anonymized: <span>Retrouvez ici <b>tous les salariés de votre service</b>. Votre service étant en <b>mode anonymisé</b>, la recherche se fait uniquement par <b>identifiant</b> (identifiant RAVAIL ou identifiant lié) : saisissez-le dans la barre de recherche puis <b>double-cliquez</b> sur une ligne pour ouvrir le dossier du salarié.</span>,
};

const tutoStateInit = {
    run: false,
    stepIndex: 0,
    steps: [
        { // TUTO_STEPS.WELCOME
            target: "#tutoButton",
            title: 'Bienvenue !',
            content: <><span>RAVAIL fait peau neuve 🎉</span><br/><br/>
                <span>Découvrons ensemble la nouvelle interface et les fonctionnalités de nos outils 😊</span></>,
        },
        { // TUTO_STEPS.NEWS
            target: 'body',
            placement: 'center',
            title: 'Les nouveautés',
            content: <span style={{display: "block", textAlign: "left"}}>
                <b>Tous les types de visites</b> sont désormais pris en charge : embauche, périodique, mi-carrière, fin de carrière, suivi post-exposition...<br/><br/>
                Les données sont <b>centralisées au niveau de votre compte utilisateur</b> : les salariés, leurs visites et leur relevé de carrière sont regroupés au même endroit.
            </span>,
        },
        { // TUTO_STEPS.NEW_SALARIE
            target: '#newSalarie',
            content: <><span>Créez ici le <b>dossier d'un salarié</b> : son identité, ses visites et son relevé de carrière y seront <b>centralisés</b>. C'est depuis ce dossier que vous pourrez créer ses visites et ses convocations.</span><br/><br/>
                <span style={{fontSize:"14px"}}>Astuce : le bouton juste à droite permet d'importer vos salariés en masse via un fichier CSV.</span></>,
        },
        { // TUTO_STEPS.SEARCH_SALARIE
            target: '#salarieTable',
            content: SEARCH_SALARIE_CONTENT.default,
        },
        { // TUTO_STEPS.TEST_VISITE
            target: '#newVisite',
            spotlightClicks: true,
            content: <><span>Pour découvrir le déroulé d'une visite sans créer de dossier salarié, vous pouvez lancer une <b>visite de test</b>.</span><br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur le bouton pour continuer le tutoriel.</b></span></>,
            hideFooter: true
        },
        { // TUTO_STEPS.VISITE_TYPE
            target: '#visiteTypeMiCarriere',
            spotlightClicks: true,
            hideBackButton: true,
            styles: { options: { zIndex: 1050 } }, // Au dessus du modal de sélection du type de visite, pour ne laisser cliquable que le bouton ciblé
            content: <><span><b>Tous les types de visites</b> sont désormais pris en charge ! Utilisez la <b>sélection rapide</b> ou recherchez le type de visite souhaité.</span><br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur « Visite de mi-carrière » pour continuer le tutoriel.</b></span></>,
            hideFooter: true
        },
        { // TUTO_STEPS.INFO_MODAL
            target: '#modalHideNextTime',
            hideBackButton: true,
            content: <span>À chaque étape de la visite, une <b>fenêtre d'information</b> est disponible pour vous aider à remplir les informations demandées.<br/>
                Lorsque vous serez à l'aise avec l'outil, n'hésitez pas à décocher la case "<b>Afficher les prochaines fenêtres d'aide</b>" !</span>,
        },
        { // TUTO_STEPS.INFO_BUTTON
            target: '#buttonModalInfo',
            spotlightClicks: true,
            hideBackButton: true,
            content: <span>
                Vous souhaitez revoir cette fenêtre d'information ?<br/>
                À tout moment, vous pouvez la consulter en cliquant sur ce bouton, essayez !<br/>
                <br/>
                De manière générale, les icônes <InfoCircleTwoTone twoToneColor="#04aceb"/> et <QuestionCircleOutlined /> sont là pour vous donner des <b>informations supplémentaires</b>, des <b>sources</b>, des <b>annexes</b>, etc.<br/>
                L'icône <CertaintyOk height="1.2em" width="1em"/> indique une <b>suggestion</b> de l'outil, n'hésitez pas à passer votre souris sur ces icônes pour obtenir le détail.
            </span>
        },
        { // TUTO_STEPS.METIER_SEARCH
            target: '#jobSearchInput',
            hideBackButton: true,
            content: <><span>C'est ici que vous recherchez et ajoutez les <b>métiers</b> exercés par le salarié, parmi des milliers de métiers référencés.</span><br/><br/>
                <span style={{fontSize:"14px"}}>Pour le tutoriel, nous avons ajouté automatiquement le métier « <b>Désamianteur</b> ». Cliquez sur « Suivant » pour le découvrir.</span></>,
        },
        { // TUTO_STEPS.METIER_PERIODES
            target: '.jobCard',
            hideBackButton: true,
            content: <><span>Pour ce tuto, nous avons automatiquement ajouté « <b>Désamianteur</b> » comme métier exercé depuis janvier 2000.</span><br/><br/>
                <span style={{fontSize:"14px"}}>Vous pouvez ajuster ces dates, ajouter d'autres périodes d'exercice, préciser le code NAF de l'entreprise, le temps de travail...</span></>,
        },
        { // TUTO_STEPS.VALIDATE_METIER
            target: '#button-validate-step-and-save',
            spotlightClicks: true,
            spotlightPadding: 4,
            hideBackButton: true,
            content: <><span>Pour <b>valider</b> une étape, cliquez sur ce bouton. Vos changements sont <b>automatiquement sauvegardés</b> à chaque validation d'étape.</span><br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur le bouton pour passer aux tâches du métier.</b></span></>,
            hideFooter: true
        },
        { // TUTO_STEPS.TACHES
            target: '#tachesContainer',
            hideBackButton: true,
            content: <span>Voici les <b>tâches</b> suggérées pour ce métier. <b>Cochez toutes les tâches</b> réalisées par le salarié : elles permettent d'affiner les expositions qui seront suggérées aux étapes suivantes.</span>,
        },
        { // TUTO_STEPS.ADD_TACHE
            target: '.addTacheButton',
            hideBackButton: true,
            content: <span>Une tâche manque dans les suggestions ? <b>Ajoutez votre propre tâche</b> en cliquant ici : vous pourrez la lier aux expositions correspondantes et la retrouver dans vos favoris pour les prochaines visites.</span>,
        },
        { // TUTO_STEPS.VALIDATE_ALL
            target: '#button-validate-all-steps',
            spotlightClicks: true,
            hideBackButton: true,
            content: <><span>Vous pouvez compléter chaque étape une à une... ou gagner du temps ! Le bouton « <b>Valider toutes les étapes</b> » complète automatiquement la visite avec les suggestions par défaut et vous amène directement à la synthèse.</span><br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur le bouton puis confirmez pour continuer le tutoriel.</b></span></>,
            hideFooter: true
        },
        { // TUTO_STEPS.SYNTHESE
            target: '#main-tabs-container',
            hideBackButton: true,
            content: <span>Vous voilà à la dernière étape ! Retrouvez ici la <b>synthèse</b> de la visite, le compte-rendu, les courriers et l'onglet de <b>téléchargement des documents</b> à remettre au salarié.</span>,
        },
        { // TUTO_STEPS.QUICK_MODE
            target: '#quickMode',
            spotlightClicks: true,
            hideBackButton: true,
            content: <span>
                Le <b>mode 'rapide'</b> est <b>activé par défaut</b> et permet de masquer certaines étapes !<br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur le bouton pour afficher <i>les étapes masquées</i> et continuer le tutoriel.</b></span>
            </span>,
            hideFooter: true
        },
        { // TUTO_STEPS.STEPS_NAV
            target: '#stepNavItem-EXAMEN_CLINIQUE_ORIENTE',
            spotlightClicks: true,
            hideBackButton: true,
            content: <><span>Bravo, de nouvelles étapes sont apparues !<br/> <br/>Pour <b>naviguer</b> entre les étapes de la visite (métiers, tâches, expositions, examens, synthèse...), vous pouvez directement <b>cliquer dessus</b> ici.</span><br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur « Examen clinique orienté » pour continuer le tutoriel.</b></span></>,
            hideFooter: true
        },
        { // TUTO_STEPS.EMAIL_PRE_VISITE
            target: '#button-send-email-pre-visite',
            spotlightClicks: true,
            hideBackButton: true,
            content: <><span>
                À n'importe quel moment, vous pouvez demander au salarié de compléter les informations déjà remplies en cliquant ici.<br/><br/>
                <span style={{fontSize:"14px"}}>Par exemple, vous pouvez ajouter des métiers puis demander au salarié de valider les tâches qu'il a réalisées.</span>
            </span><br/><br/>
                <span style={{fontSize:"14px"}}><b>Cliquez sur le bouton pour continuer le tutoriel.</b></span></>,
            hideFooter: true
        },
        { // TUTO_STEPS.EMAIL_READY
            target: '#qrCodeMail',
            hideBackButton: true,
            content: <span>Un <b>email de pré-visite</b> vient d'être généré : il contient le <b>lien d'accès</b> et un <b>QR code</b> permettant au salarié de compléter ses informations.</span>,
        },
        { // TUTO_STEPS.END
            target: '#returnToVisiteButton',
            hideBackButton: true,
            title: 'À vous de jouer !',
            content: <span>Et voilà ! Vous êtes prêt à utiliser nos outils. Vous pouvez <b>retourner à la visite</b> en cliquant ici.<br/>
                N'hésitez pas à nous contacter (01 84 80 80 41 | contact@ravail.fr) si vous avez des questions ou des suggestions d'amélioration.<br/>
                Bonnes visites 🩺!</span>,
        }
    ],
    active: false,
};

function AppContextProvider({ children, ...props }) {
    const [outil, setOutil] = useState();
    const [tutoState, setTutoState] = useState(tutoStateInit);

    const value = useMemo(() => ({ outil, setOutil, tutoState, setTutoState }), [outil, setOutil, tutoState, setTutoState]);

    return (
        <AppContext.Provider value={value} {...props}>
            {children}
        </AppContext.Provider>
    )
}

export default AppContextProvider;
